import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'

import { ApiError, api } from '~/api/client'
import type { PublicCalendarEntry, ShareDraft, SharePayload, ShareFrame, SharePool } from '~/api/types'
import { Search } from '~/components/Search'
import { SHARE_PAGE } from '~/share/SharePage'
import { frameFor } from '~/share/Frame'
import type { Chosen, Template } from '~/share/catalogue'
import {
  NOTHING,
  TEMPLATES,
  cardPath,
  cardRows,
  draftPath,
  formatOf,
  frameLine,
  frameOf,
  ready,
} from '~/share/catalogue'
import { useViewer } from '~/Viewer'

import s from './Studio.module.css'

/*
 * Where the Instagram cards are made.
 *
 * The six cards the site draws have been reachable only by typing a `/share/…` address from
 * memory: nothing links to them and nothing lists them. This is the page that lists them,
 * takes the one argument each needs, shows the card, and hands over the PNG.
 *
 * **It draws no card markup of its own.** The preview is `/share-page/<card>` in an iframe —
 * the same document headless Chrome photographs, differing from the download address by that
 * prefix and nothing else. So there is no second copy of a card here to drift from the first,
 * and every fact on screen came from `/share-data/` through the card's own components.
 */

/**
 * How long a run of ticks settles before the selection is written.
 *
 * Every tick is a card he could mean, and a write turns one into an address: the first mints
 * the row and every one after it bumps that row's version, which is the filename and the memo
 * key. Ticking four rows in a second is one card he meant and three he passed through, so the
 * write waits for the pressing to stop.
 *
 * What this bounds is rows, versions and requests, not browsers. A write renders nothing —
 * `_share_png` is the only caller of the renderer and `/share/` is the only route that reaches
 * it, while the preview is the `/share-page/` document — so a tick has never cost a Chrome and
 * a debounce is not what stops it costing one.
 */
const SETTLE = 300

/**
 * The card the studio is pointed at, once the server has agreed it exists.
 *
 * `'asked'` is not the same as `'drawing'`: this is the JSON question — does this card exist,
 * and what week did the route fold my night onto — which costs no browser. The PNG is a
 * separate press, because a browser on the box is the expensive thing and changing a template
 * must not spend one.
 */
type Card =
  | { status: 'asked' }
  | { status: 'no'; why: string }
  | { status: 'yes'; payload: SharePayload }

export function Studio() {
  const me = useViewer()
  if (me === undefined) return <main className={s.wrap} aria-busy="true" />
  if (!me?.isEditor) {
    // The editor's page, said by the page rather than hidden by the route — a typed address
    // should answer. `me.isEditor` and nothing else, so normie mode takes the studio away
    // with every other editorial control: the mask in `ViewerProvider` is what does it.
    return (
      <main className={s.wrap}>
        <h1 className={s.head}>Studio</h1>
        <p className={s.said}>Not yours. This is where the editor makes the Instagram cards.</p>
      </main>
    )
  }
  // A separate component, so nothing that fetches a card or a picture is ever mounted for
  // somebody who is not the editor — a guard inside an effect would be one `&&` from asking.
  return <Desk />
}

/**
 * The rows the card's own rule draws out of this pool: the prefix a chosen set replaces, and
 * therefore what the tick-boxes start on.
 *
 * Both halves are the server's word and neither is worked out here — `pick` per night for the
 * week, which is the one row the rule draws there, and `drawn` for the four that draw one
 * list, whose default is that many of it from the front. A second expression of either would
 * be a copy of `_share_pool`'s rule with nothing keeping the two in step. A night may end up
 * carrying two stops; never by the rule, so never in what the boxes open on.
 */
export function rulePicks(pool: SharePool | null): string[] {
  if (!pool) return []
  if (pool.days) return pool.days.flatMap(day => (day.pick ? [day.pick] : []))
  return (pool.candidates ?? []).slice(0, pool.drawn ?? 0).map(row => row.id)
}

/**
 * Whether a selection is the rule's own, in which case there is nothing to write: the card is
 * drawn at its own address and no row is spent saying what the rule already says.
 *
 * Order counts for the four that draw one list, because the order he pressed is the order
 * drawn — reticking a row he unticked puts it at the end, which is a different card and has to
 * read as one. It does not count for the week: a stop can only be drawn on its own night, and
 * two on one night are drawn in that night's rank order, so the same ids in any order draw the
 * same line.
 */
function sameRows(mine: string[], theirs: string[], byNight: boolean): boolean {
  if (mine.length !== theirs.length) return false
  if (!byNight) return mine.every((id, at) => id === theirs[at])
  const held = new Set(theirs)
  return mine.every(id => held.has(id))
}

function Desk() {
  const [card, setCard] = useState<Template>(TEMPLATES[0])
  const [chosen, setChosen] = useState<Chosen>(NOTHING)
  const [state, setState] = useState<Card>({ status: 'asked' })
  /** What the search said about a result that is not what this template needs. */
  const [aside, setAside] = useState('')
  const [drawing, setDrawing] = useState(false)
  const [trouble, setTrouble] = useState('')
  /** The card's own candidates, and the route's word if it would not answer them. */
  const [pool, setPool] = useState<SharePool | null>(null)
  const [poolWhy, setPoolWhy] = useState('')
  /**
   * The rows he has kept, in the order he kept them — starting at the rule's own, because the
   * boxes are ticked to show what is on the card and unticking one has to be a card without it.
   */
  const [keeping, setKeeping] = useState<string[]>([])
  /** The draft his rows were written to, once they have stopped being the rule's own. */
  const [draft, setDraft] = useState<ShareDraft | null>(null)

  // Two addresses, and the difference is the whole of this slice: the card's own — which the
  // pool is asked for and which a draft row stores — and the one being drawn, which is the
  // draft's when there is one. The frame and the slide ride on the second either way.
  const own = cardRows(card, chosen)
  const rest = draft ? draftPath(card, chosen, draft.token) : cardPath(card, chosen)
  const complete = ready(card, chosen)
  const { width, height } = frameFor(formatOf(card, chosen), frameOf(card, chosen))
  /** Whether this card has rows to choose between at all: five do, `One gig` does not. */
  const listed = card.rows === 'list' && complete
  const theirs = rulePicks(pool)
  const mine = pool !== null && !sameRows(keeping, theirs, pool.days !== undefined)
  /**
   * The draft's version, and why the preview is keyed on it as well as on the address.
   *
   * A rewrite keeps the token, so `rest` is the same `d/<token>` before and after — a refetch
   * and an iframe keyed on the address alone would both sit still and leave the rows from
   * before the last tick on screen. The version is not *in* the address because the row is
   * what holds the rows: the address carries only what a stranger may vary.
   */
  const version = draft?.version ?? 0

  useEffect(() => {
    if (!complete) return
    let live = true
    setState({ status: 'asked' })
    // The query goes as one string, split back off the path the same way `SharePage` does,
    // so the studio asks the question in exactly the spelling it will show and download.
    const cut = rest.indexOf('?')
    const path = cut === -1 ? rest : rest.slice(0, cut)
    const query = cut === -1 ? '' : rest.slice(cut)
    api
      .share(path, query)
      .then(payload => {
        if (live) setState({ status: 'yes', payload })
      })
      .catch((error: unknown) => {
        // The route's own words, which is what `ApiError.detail` is for: "no such card",
        // "on must be a night, YYYY-MM-DD". This page has no better sentence to offer and
        // inventing one would be a second set of the route's rules.
        if (live) {
          setState({
            status: 'no',
            why: error instanceof ApiError ? error.detail : String(error),
          })
        }
      })
    return () => {
      live = false
    }
  }, [rest, version, complete])

  // The candidates, for whichever card's own address is on screen. Here rather than inside the
  // control that draws them, because what the rule picks is what the tick-boxes start on and
  // what "he has changed something" is measured against — and the write below needs both.
  useEffect(() => {
    if (!listed) {
      setPool(null)
      setPoolWhy('')
      return
    }
    let live = true
    setPool(null)
    setPoolWhy('')
    api
      .sharePool(own)
      .then(answer => {
        if (!live) return
        // Together, so no render sees the new pool beside the last card's kept rows: the
        // write below would read that pair as a selection he made and spend a row on it.
        setPool(answer)
        setKeeping(rulePicks(answer))
      })
      .catch((error: unknown) => {
        if (live) setPoolWhy(error instanceof ApiError ? error.detail : String(error))
      })
    return () => {
      live = false
    }
  }, [own, listed])

  /**
   * The token being rewritten, in a ref and not in state: the write below would re-arm itself
   * on its own answer if the token it reads were a dependency, and a rewrite is the difference
   * between one row for a session of ticking and a row a tick.
   *
   * Carried with the card it was written for, because that is what makes a late answer safe to
   * take: a draft is a selection for one card and the write route refuses a token pointed at
   * another, so a token whose card is no longer on screen is ignored rather than reused.
   */
  const written = useRef<{ card: string; token: string } | null>(null)

  useEffect(() => {
    if (!listed || pool === null) return
    if (!mine) {
      // Back to what the rule picks, so back to the card's own address: a row saying exactly
      // what the rule says is a name for a picture that already has one.
      setDraft(null)
      return
    }
    let live = true
    const timer = setTimeout(() => {
      setTrouble('')
      const cut = own.indexOf('?')
      const token = written.current?.card === own ? written.current.token : ''
      api
        .shareDraft({
          card: cut === -1 ? own : own.slice(0, cut),
          query: cut === -1 ? '' : own.slice(cut + 1),
          ids: keeping,
          ...(token ? { token } : {}),
        })
        .then(made => {
          if (!live) return
          written.current = { card: own, token: made.token }
          setDraft(made)
        })
        // The route's own words again: "choose at least one gig", and "this card draws at
        // most N of them" — N interpolated from the card's own `stops`, so the sentence the
        // editor reads is the under-list's own number, never one this page holds. The second
        // of those cannot reach him on a week, whose bound is its own pool: a selection drawn
        // from the candidates cannot be larger than the candidates. The page has no better
        // sentence and a second copy of its rules would be a second set of them — which is
        // also why unticking the last row is left to the route to refuse rather than caught
        // here.
        .catch((error: unknown) => {
          if (live) setTrouble(error instanceof ApiError ? error.detail : String(error))
        })
    }, SETTLE)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [keeping, own, listed, mine, pool])

  /** A card whose pool is not the one on screen has no kept rows: the next pool says what. */
  const forget = () => {
    setDraft(null)
    setKeeping([])
  }

  const pick = (next: Template) => {
    setCard(next)
    setChosen(NOTHING)
    setAside('')
    setTrouble('')
    // A draft is a selection for one card. Carrying it across a change of template would point
    // the next card at rows chosen from another card's pool, which the write route refuses —
    // and would refuse a beat too late to be a good answer.
    forget()
  }

  const download = () => {
    setDrawing(true)
    setTrouble('')
    api
      .cardPng(rest)
      .then(({ blob, filename }) => {
        // The name is the route's, off the header, so two variants of one card do not land
        // on a phone with one name between them.
        const held = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = held
        link.download = filename
        link.click()
        URL.revokeObjectURL(held)
      })
      .catch((error: unknown) => {
        setTrouble(error instanceof ApiError ? error.detail : String(error))
      })
      .finally(() => setDrawing(false))
  }

  return (
    <main className={s.wrap}>
      <h1 className={s.head}>Studio</h1>
      <p className={s.said}>
        The cards the site draws, ready to photograph. Pick one, give it what it needs, and
        take the PNG to your phone.
      </p>

      <div className={s.cols}>
        <div>
          {/* Labelled, so "the buttons that choose a card" is a thing a reader's screen
              reader and a test can both name: story-or-post and the candidates below are
              pressed-state buttons too. */}
          <ul className={s.list} aria-label="Cards">
            {TEMPLATES.map(one => (
              <li key={one.kind}>
                <button
                  type="button"
                  className={s.pick}
                  aria-pressed={one.kind === card.kind}
                  onClick={() => pick(one)}
                >
                  <span className={s.title}>{one.title}</span>
                  {/* The frames it draws in, stated in the list and chosen below: a template
                      with one says that one as a fact, because two controls that draw the same
                      card would be a control that does nothing. */}
                  <span className={s.frame}>
                    {one.frames.map((f) => frameLine(one, NOTHING, f)).join(' · ')}
                  </span>
                  <span className={s.what}>{one.what}</span>
                </button>
              </li>
            ))}
          </ul>

          <Shape
            card={card}
            chosen={chosen}
            onFrame={frame => {
              setChosen({ ...chosen, frame })
              setTrouble('')
            }}
          />

          <Argument
            card={card}
            chosen={chosen}
            onChosen={next => {
              setChosen(next)
              setAside('')
              setTrouble('')
              // The rows were chosen out of the pool of the card as it was argued: another week,
              // another amount or another room is another pool, and a draft against the old one
              // names rows this card's rule never offered. Asked as "is this the same pool?"
              // rather than "did anything change?", because a slide is *not* another pool — it
              // is one night of the week these stops were already chosen for, which is why
              // `cardRows` leaves it off and why typing one keeps the draft.
              if (cardRows(card, next) !== own) forget()
            }}
            onAside={setAside}
          />
          {aside ? <p className={s.aside}>{aside}</p> : null}

          {poolWhy ? <p className={s.trouble}>{poolWhy}</p> : null}
          {listed && pool ? (
            <Rows
              pool={pool}
              keeping={keeping}
              mine={mine}
              onKeeping={setKeeping}
              onTheirs={() => setKeeping(theirs)}
            />
          ) : null}

          <p className={s.address}>
            <code>/share/{rest}</code>
          </p>
          {state.status === 'no' ? <p className={s.trouble}>{state.why}</p> : null}
          {state.status === 'yes' ? <Facts payload={state.payload} /> : null}

          <button
            type="button"
            className={s.take}
            disabled={drawing || !complete || state.status !== 'yes'}
            onClick={download}
          >
            {drawing ? 'Drawing…' : `Download the PNG (${frameLine(card, chosen)})`}
          </button>
          {trouble ? <p className={s.trouble}>{trouble}</p> : null}
        </div>

        {/* The frame's own pixels, handed to the stylesheet as two numbers rather than drawn
            into an inline width: how big the card is *shown* is then one declaration the
            breakpoint can change, and how big it *is* stays the frame's. */}
        <div className={s.stage} style={{ '--w': width, '--h': height } as CSSProperties}>
          {complete && state.status === 'yes' ? (
            <iframe
              /* Keyed on the address and the draft's version so a changed card is a fresh
                 document rather than a navigation inside the last one, which leaves the
                 previous card on screen while the new one fetches — and so that a rewrite,
                 which changes the rows under an unchanged `d/<token>`, is a change at all. */
              key={`${rest} ${version}`}
              className={s.paper}
              title="the card"
              src={`${SHARE_PAGE}${rest}`}
            />
          ) : null}
        </div>
      </div>
    </main>
  )
}

/**
 * Which gigs go on the card.
 *
 * The candidates are the card's own rule's — `/share-pool/` answers the pool the card takes its
 * prefix of — so everything offered here is a row the card would have been allowed to draw.
 * They open on the rule's own picks, ticked. That is what the card already shows, so the one
 * gesture the control has to answer is "not that one" — and a box that started empty beside a
 * card drawing four rows would be a control disagreeing with the picture next to it.
 *
 * Two shapes, because two geometries: the week's line is grouped by night and a stop can only
 * be drawn on its own night, so it offers its candidates night by night; the other four are one
 * list in an order, and the order is the order he pressed. Night by night is the grouping and
 * no longer a limit of one — a night may carry two stops, and the server puts a night's chosen
 * rows in the night's own rank order, so which of them he pressed first changes nothing.
 *
 * A press is the whole gesture: `Desk` writes what it is holding once the pressing settles and
 * the card redraws. Nothing here knows that, which is why this takes the pool rather than
 * fetching it — one place decides what the rule picked, and it is the place that writes.
 */
function Rows({
  pool,
  keeping,
  mine,
  onKeeping,
  onTheirs,
}: {
  pool: SharePool
  keeping: string[]
  /** Whether what is ticked has stopped being the rule's own: the way back is offered then. */
  mine: boolean
  onKeeping: (ids: string[]) => void
  onTheirs: () => void
}) {
  const nights = pool.days
  const held = new Set(keeping)

  /** Press a candidate: on, or off. One gesture on every card, the week included. */
  const press = (id: string) => {
    onKeeping(held.has(id) ? keeping.filter(one => one !== id) : [...keeping, id])
  }

  return (
    <div className={s.arg}>
      {/* What is ticked is what is on the card, so there is no rule left for the label to
          explain: "keep none for the calendar's own picks" was true while the boxes opened
          empty and is the opposite of true now that they open on those picks. What is worth
          saying is how many of these the card will take, and it is the pool's own number
          either way — a cap for the four cards drawn for a number of rows, and for the week
          the size of the pool itself, because its cover resizes to hold whatever it is given
          and the only bound left is this list. Two sentences and not one: a page that said
          "draws 50" of a week would be claiming a limit the card does not have. */}
      <span className={s.label}>
        Which gigs —{' '}
        {nights ? `the card draws all ${pool.stops} if you tick them` : `the card draws ${pool.stops}`}
      </span>
      {nights ? (
        <ul className={s.nights} aria-label="The week, night by night">
          {nights.map(day => (
            <li key={day.night}>
              <span className={s.night}>{day.night}</span>
              {day.candidates.length === 0 ? (
                <span className={s.chosen}>nothing picked</span>
              ) : (
                <div className={s.candidates}>
                  {day.candidates.map(row => (
                    <Candidate
                      key={row.id}
                      row={row}
                      on={held.has(row.id)}
                      onPress={() => press(row.id)}
                    />
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className={s.candidates}>
          {(pool.candidates ?? []).map(row => (
            <Candidate
              key={row.id}
              row={row}
              on={held.has(row.id)}
              at={keeping.indexOf(row.id)}
              onPress={() => press(row.id)}
            />
          ))}
        </div>
      )}
      {mine ? (
        <div className={s.choices}>
          {/* One press back to what the rule picked, and drawn only while there is somewhere
              to go back to: a button that restores the state you are already in is a button
              that does nothing. There is no button *forward* — the card follows the ticks. */}
          <button type="button" className={s.choice} onClick={onTheirs}>
            The rule&rsquo;s own
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * One candidate, and where it sits when he has kept it.
 *
 * `at` is its place in his order, printed for the three cards whose drawn order is his — the
 * week and just announced draw in night order whoever chose them, so nothing is numbered there.
 */
function Candidate({
  row,
  on,
  at,
  onPress,
}: {
  row: PublicCalendarEntry
  on: boolean
  at?: number
  onPress: () => void
}) {
  return (
    <button type="button" className={s.candidate} aria-pressed={on} onClick={onPress}>
      {on && at !== undefined && at >= 0 ? <span className={s.at}>{at + 1}</span> : null}
      <span className={s.who}>{row.title}</span>
      <span className={s.when}>
        {row.dowShort} {row.dayNum} · {row.venue}
      </span>
    </button>
  )
}

/**
 * Story or post, where that is a choice.
 *
 * Drawn from `card.frames`, which is the browser's half of the server's own list, so the page
 * offers exactly the frames the route draws and every label comes out of `Frame`'s two sizes.
 * One frame is stated rather than offered: a pair of buttons whose two answers produce the same
 * card is a control that does nothing, and the spec says so in as many words.
 */
function Shape({
  card,
  chosen,
  onFrame,
}: {
  card: Template
  chosen: Chosen
  onFrame: (frame: ShareFrame) => void
}) {
  const here = frameOf(card, chosen)
  if (card.frames.length === 1) {
    return (
      <p className={s.arg}>
        <span className={s.label}>Shape</span>
        <span className={s.chosen}>{frameLine(card, chosen)}</span>
      </p>
    )
  }
  return (
    <div className={s.arg}>
      <span className={s.label}>Story or post</span>
      <div className={s.choices} role="group" aria-label="Story or post">
        {card.frames.map(frame => (
          <button
            key={frame}
            type="button"
            className={s.choice}
            aria-pressed={frame === here}
            onClick={() => onFrame(frame)}
          >
            {frameLine(card, chosen, frame)}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * What the chosen template has to be given.
 *
 * A gig and a room come through the site's own search, so the matching is `slugs.fold`'s and
 * `café oto` resolves here exactly as it does on the bar. A week is a date, an amount is a
 * number, and neither is checked against the route's rules here — the route says them.
 */
function Argument({
  card,
  chosen,
  onChosen,
  onAside,
}: {
  card: Template
  chosen: Chosen
  onChosen: (next: Chosen) => void
  onAside: (said: string) => void
}) {
  const search = (
    <Search
      /* Nobody is being looked for: a person is not a card. */
      people={false}
      onGig={id =>
        card.needs === 'gig'
          ? onChosen({ ...chosen, value: id })
          : onAside('That is a gig — this template wants a room.')
      }
      onVenue={slug =>
        card.needs === 'venue'
          ? onChosen({ ...chosen, value: slug })
          : onAside('That is a room — this template wants a gig.')
      }
      onArtist={() => onAside('There is no card for an act yet — pick one of their gigs.')}
      onGenre={() => onAside('There is no card for a genre.')}
      onPerson={() => onAside('There is no card for a person.')}
      onEverything={() => onAside('Pick one of the results to put it on the card.')}
    />
  )

  if (card.needs === 'gig' || card.needs === 'venue') {
    return (
      <div className={s.arg}>
        <label className={s.label} htmlFor="studio-search">
          {card.needs === 'gig' ? 'Which gig' : 'Which room'}
        </label>
        <div id="studio-search">{search}</div>
        {chosen.value ? <p className={s.chosen}>{chosen.value}</p> : null}
      </div>
    )
  }

  if (card.needs === 'pounds') {
    return (
      <div className={s.arg}>
        <label className={s.label} htmlFor="studio-pounds">
          Under how many pounds
        </label>
        <input
          id="studio-pounds"
          className={s.field}
          value={chosen.value}
          inputMode="numeric"
          placeholder="15"
          onChange={event => onChosen({ ...chosen, value: event.target.value.trim() })}
        />
      </div>
    )
  }

  if (card.needs === 'week') {
    return (
      <div className={s.arg}>
        <label className={s.label} htmlFor="studio-week">
          Any night in the week — blank for this one
        </label>
        <input
          id="studio-week"
          className={s.field}
          type="date"
          value={chosen.value}
          onChange={event => onChosen({ ...chosen, value: event.target.value })}
        />
        {card.slide ? (
          <>
            <label className={s.label} htmlFor="studio-slide">
              {card.slide.label}
            </label>
            <input
              id="studio-slide"
              className={s.field}
              value={chosen.slide}
              inputMode="numeric"
              placeholder="blank for the cover"
              onChange={event => onChosen({ ...chosen, slide: event.target.value.trim() })}
            />
          </>
        ) : null}
      </div>
    )
  }

  if (card.needs === 'since') {
    return (
      <div className={s.arg}>
        <label className={s.label} htmlFor="studio-since">
          Announced since — blank for the route&rsquo;s own window
        </label>
        <input
          id="studio-since"
          className={s.field}
          type="date"
          value={chosen.value}
          onChange={event => onChosen({ ...chosen, value: event.target.value })}
        />
      </div>
    )
  }

  return <p className={s.arg}>Nothing to choose: this card is about tonight.</p>
}

/**
 * The one or two facts worth reading off the answer before pressing download — chiefly which
 * week a night was folded onto, because the route takes any night and draws its ISO week.
 *
 * Read off the payload rather than worked out here: the studio formats nothing. A price, a
 * night and a room are the card's to print, and this is only ever the span the route chose.
 */
function Facts({ payload }: { payload: SharePayload }) {
  if (payload.kind === 'week' || payload.kind === 'under') {
    return (
      <p className={s.said}>
        The week of {payload.first} to {payload.last}.
      </p>
    )
  }
  if (payload.kind === 'announced') {
    return <p className={s.said}>New since {payload.since}.</p>
  }
  if (payload.kind === 'room') {
    return <p className={s.said}>{payload.name}.</p>
  }
  if (payload.kind === 'gig') {
    return <p className={s.said}>{payload.event.title}.</p>
  }
  return <p className={s.said}>Tonight, {payload.night}.</p>
}
